import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { SignIn } from '../onboarding/screens/SignIn'
import { MagicLinkSent } from '../onboarding/screens/MagicLinkSent'
import { AVAILABLE_PROVIDERS } from '../onboarding/flow'
import type { ProviderId } from '../onboarding/flow'
import { getAuthConfig, requestMagicLink, startEntraSignIn } from '../lib/api'
import { SUPPORT_EMAIL } from '../lib/support'
import { Turnstile } from '../ui'
import { SignInShell } from './SignInShell'
import '../onboarding/onboarding.css'

/**
 * Why an Entra round trip came back without signing anyone in.
 *
 * The API distinguishes exactly three cases and no more. "Refused" and "failed" are separated
 * because one is the person's own choice and the other is not; every genuine failure — unknown
 * state, wrong browser, bad signature, expired code — collapses into `entra_failed`, because
 * telling an attacker which of their attempts got furthest helps only them.
 *
 * `entra_unavailable` is ours. It is here so a customer is never told to try again against a
 * certificate that does not exist.
 *
 * `entra_not_linked` is the fourth, and it breaks the rule above on purpose. Every other failure
 * is deliberately vague; this one names itself because the person reading it is almost always a
 * real customer who has simply not attached this Microsoft account yet, and who has a working
 * alternative sitting on the same page. Collapsing it into `entra_failed` would tell them to
 * "start again" at something that will refuse them every time.
 *
 * ITS TEXT WAS WRONG UNTIL 0104 AND SAID SO CONFIDENTLY: it told people Microsoft sign-in
 * would start working once an administrator connected the organisation's directory. That was
 * true of migration 0101's rule and false of the one that replaced it -- connecting a
 * directory has nothing to do with attaching an identity to an account. Pointing somebody at
 * a remedy that cannot work is worse than the vagueness this message exists to avoid. See
 * migration 0104 for what the
 * refusal protects.
 */
const NOTICES: Record<string, string> = {
  entra_refused: 'Microsoft sign-in was cancelled. You can try again, or use a sign-in link.',
  entra_failed:
    'That Microsoft sign-in could not be completed. Start again from this page — links and ' +
    'sign-in attempts are single-use, so an old one will not work twice.',
  entra_unavailable:
    'Microsoft sign-in is temporarily unavailable on our side. A sign-in link will still work.',
  entra_not_linked:
    'That Microsoft account is not connected to a Defenssive account yet. Sign in with a ' +
    'link, then choose Connect Microsoft sign-in in Settings, under Your account — that is what attaches ' +
    'it, and only you can do it from your own session.',
}

/** Shown after an owner closes their workspace (CloseWorkspaceCard lands here). Access shutdown, not deletion. */
const WORKSPACE_CLOSED_NOTICE =
  'Your workspace was closed and everyone in it has been signed out. Its stored data was not ' +
  `deleted — email ${SUPPORT_EMAIL} if you want it removed. You can still sign in to any other ` +
  'workspace you belong to.'

/** Shown after "Switch workspace" (TopBar): a sign-in stays in one workspace, so switching is signing in again. */
const SWITCH_NOTICE =
  'You are signed out. Sign in again and choose which workspace to open — each sign-in stays in ' +
  'one workspace.'

/**
 * Shown after "Sign out" (TopBar), which only sends people here once the server has confirmed it.
 * Without it the page looked the same as any visit, so nothing said the sign-out had happened.
 */
const SIGNED_OUT_NOTICE = 'You are signed out of Defenssive.'

/**
 * The bot check refused the request (the token was missing, expired or already used). Safe to say
 * plainly: the refusal depends on the token alone, never on the address, so it tells nobody whether an
 * address has an account.
 */
const CHALLENGE_FAILED_NOTICE =
  'The security check did not complete, so no link was requested. Complete the check again, then ' +
  'request the link.'

/**
 * Cloudflare could not be reached, or our own setting for it is wrong. Not the person's doing and not a
 * pass, so no link was sent. Microsoft sign-in does not use the check.
 */
const CONFIG_RETRY_MS = 1_500

/**
 * The sign-in page could not ask Defenssive how it is set up (twice), so it cannot know whether the
 * security check is on. No link can be requested until it knows; Microsoft sign-in does not depend on it.
 */
const CONFIG_FAILED_NOTICE =
  'We could not load the sign-in settings from Defenssive, so no link can be requested yet. Check your ' +
  'connection and reload this page. If your account already has a Microsoft account connected, you can ' +
  'sign in with it now.'

const CHALLENGE_UNAVAILABLE_NOTICE =
  'The security check is unavailable just now, so no link was requested. Please try again in a few ' +
  'minutes. If your account already has a Microsoft account connected, you can sign in with it now.'

/**
 * The real sign-in page.
 *
 * Shares the SignIn screen with the prototype so the two cannot drift apart visually, but every
 * action here talks to the API. It is a plain page rather than a step in a flow: the Entra
 * callback redirects here on failure, and a page that only existed inside a wizard would have
 * nowhere to land.
 */
export function SignInPage() {
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [sent, setSent] = useState(false)
  const [code, setCode] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [resent, setResent] = useState(false)

  // The bot check. The site key comes from the API so turning the check on is a setting, not a rebuild.
  // THREE states, not two: until the answer is had the page does not know whether the check is on, and
  // "could not be had" is not "off". Treating either as off would let a request go out with no token that
  // the server then refuses, and say "complete the check" with no widget on the page to complete.
  const [configState, setConfigState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [siteKey, setSiteKey] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [challengeReset, setChallengeReset] = useState(0)

  useEffect(() => {
    let live = true

    async function load() {
      // One retry, shortly after: a blip on first load should not cost the person a reload.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const config = await getAuthConfig()
        if (!live) return

        if (config.reached) {
          setSiteKey(config.turnstileSiteKey)
          setConfigState('ready')
          return
        }

        if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, CONFIG_RETRY_MS))
        if (!live) return
      }

      setConfigState('failed')
    }

    void load()

    return () => {
      live = false
    }
  }, [])

  // A link cannot be requested while the page does not yet know whether the check is on, has failed to
  // find out, or the check is on and not yet passed.
  const challengePending = configState !== 'ready' || (siteKey !== null && token === null)

  // A passed check ends the "complete the check again" notice that asked for it.
  function handleToken(next: string | null) {
    setToken(next)
    if (next) {
      setNotice((current) =>
        current === CHALLENGE_FAILED_NOTICE || current === CHALLENGE_UNAVAILABLE_NOTICE ? null : current,
      )
    }
  }

  // The reason, in words, only when the check is what is holding the button (not while the page is still
  // finding out, which is brief, and not when the settings could not be loaded, which has its own notice).
  const challengeHint =
    configState === 'ready' && siteKey !== null && token === null
      ? 'Complete the security check above to continue.'
      : null

  const challenge = siteKey ? (
    <Turnstile siteKey={siteKey} onToken={handleToken} resetKey={challengeReset} />
  ) : null

  const error = params.get('error')
  // Arriving from closing a workspace is not an error, but it is the one thing the person most needs
  // confirmed: the close landed, and why they are signed out.
  const closed = params.get('workspace_closed') === '1'
  const switching = params.get('switch') === '1'
  const signedOut = params.get('signed_out') === '1'
  const message =
    notice ??
    (error ? NOTICES[error] ?? NOTICES.entra_failed : null) ??
    (closed ? WORKSPACE_CLOSED_NOTICE : null) ??
    (switching ? SWITCH_NOTICE : null) ??
    (signedOut ? SIGNED_OUT_NOTICE : null) ??
    (configState === 'failed' ? CONFIG_FAILED_NOTICE : null)
  // Confirmations of something the person did are not errors. Only a failure (a sign-in error or
  // a request that did not go through) keeps the warning style.
  const informational = !notice && !error && (closed || switching || signedOut)
  // Both "I just signed out" and "my workspace was just closed" mean every session was revoked; a
  // silent Microsoft round trip would drop the person straight back in, which reads as nothing having
  // happened. Ask Microsoft to actually re-authenticate.
  const forceReauth = signedOut || closed

  async function handleProvider(provider: ProviderId) {
    // Only Entra is built, and only built providers are drawn here, so this is defence against a
    // future edit rather than something a user can reach.
    if (provider !== 'entra') return

    setBusy(true)
    setNotice(null)

    // Signed out a moment ago? Then ask Microsoft to actually re-authenticate rather than
    // answering from its own cookie. Without this the product's own sign-out worked perfectly --
    // session revoked, new one issued -- and the person was still returned to their dashboard
    // without being asked anything, which is indistinguishable from sign-out not working.
    const url = await startEntraSignIn(forceReauth)

    if (!url) {
      setBusy(false)
      setNotice(NOTICES.entra_unavailable ?? null)
      return
    }

    // assign, not replace: the browser's Back button should return here rather than skipping
    // past the sign-in page entirely.
    window.location.assign(url)
  }

  async function handleMagicLink() {
    setBusy(true)
    setNotice(null)

    const { reachedServer, confirmationCode, challenge: outcome } = await requestMagicLink(
      email,
      company,
      token,
    )

    setBusy(false)

    // A token works once, whatever the answer was, so the person is asked for a new one every time.
    setToken(null)
    setChallengeReset((n) => n + 1)

    // BEFORE "reached the server": a refused check (400) is a received request but not a sent link, and
    // falling through would announce "check your inbox" for mail nobody asked for.
    if (outcome === 'failed') {
      setNotice(CHALLENGE_FAILED_NOTICE)
      return
    }

    if (outcome === 'unavailable') {
      setNotice(CHALLENGE_UNAVAILABLE_NOTICE)
      return
    }

    if (!reachedServer) {
      // The request never landed, which is nothing to do with the address — it fails identically
      // for one that does not exist, so saying so leaks nothing. Telling someone to check their
      // inbox for mail that was never requested would be the worse answer.
      setNotice('We could not reach Defenssive just now. Please try again in a moment.')
      return
    }

    // Otherwise, unconditionally. The endpoint answers 202 with the same body whether the
    // address is known, suppressed or rate limited — a UI that showed a different screen for any
    // of those would rebuild the account-enumeration oracle the API is careful not to be.
    setCode(confirmationCode)
    // A second request from the "check your inbox" screen is a resend, and that screen says so.
    setResent(sent)
    setSent(true)
  }

  if (sent) {
    return (
      <SignInShell>
        <MagicLinkSent
          email={email.trim()}
          simulated={false}
          confirmationCode={code}
          onResend={handleMagicLink}
          onDifferentMethod={() => {
            setSent(false)
            setResent(false)
            setNotice(null)
          }}
          busy={busy}
          notice={notice}
          resent={resent}
          challenge={challenge}
          challengePending={challengePending}
          challengeHint={challengeHint}
        />
      </SignInShell>
    )
  }

  return (
    <SignInShell>
      <SignIn
        email={email}
        company={company}
        onEmailChange={setEmail}
        onCompanyChange={setCompany}
        onProvider={handleProvider}
        onMagicLink={handleMagicLink}
        notice={message}
        noticeTone={informational ? 'info' : 'warn'}
        busy={busy}
        challenge={challenge}
        challengePending={challengePending}
        challengeHint={challengeHint}
        // Only providers that sign someone in. Google Workspace and Okta are designed, not built; the
        // prototype still shows them disabled, but a customer's sign-in page is not the place for them.
        providers={AVAILABLE_PROVIDERS}
      />
    </SignInShell>
  )
}
